import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';
import Header from '../components/Header';
import HeroSection from '../components/HeroSection';
import CTABanner from '../components/CTABanner';
import TrustStats from '../components/TrustStats';
import ServicesSection from '../components/ServicesSection';
import RegionsSection from '../components/RegionsSection';
import AboutSection from '../components/AboutSection';
import TestimonialsSection from '../components/TestimonialsSection';
import BlogSection from '../components/BlogSection';
import ContactSection from '../components/ContactSection';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const [homeContent, setHomeContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const t = translations[language];
  const location = useLocation();

  // Helper function to get translated content
  const getTranslated = (obj, field) => {
    if (!obj) return '';
    if (language === 'en' && obj[`${field}_en`]) {
      return obj[`${field}_en`];
    }
    return obj[field] || '';
  };

  useEffect(() => {
    const fetchHomeContent = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/home-content`);
        setHomeContent(response.data);
      } catch (error) {
        console.error('Error fetching home content:', error);
        // Use defaults if API fails
        setHomeContent({
          hero: {
            title: "Bem-vindo à Your Broker Portugal",
            subtitle: "Encontre a propriedade dos seus sonhos em Portugal",
            image: "https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=1200&q=80",
            ctaText: "Contacte-nos",
            ctaLink: "#contact"
          },
          intro: {
            title: "A Sua Jornada Começa Aqui",
            description: "Especializados no mercado imobiliário português."
          },
          about: {
            title: "Sobre Nós",
            description: "Especialistas em imobiliário de luxo.",
            highlight: "Excelência e Confiança",
            image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80"
          },
          stats: {
            stat1_value: "500+",
            stat1_label: "Propriedades",
            stat2_value: "15+",
            stat2_label: "Anos",
            stat3_value: "98%",
            stat3_label: "Satisfação"
          },
          headerFooter: {
            companyName: "Your Broker Portugal",
            footerDescription: "Especialistas em imobiliário de luxo",
            phone: "+351 XXX XXX XXX",
            email: "info@yourbrokerportugal.com",
            address: "Lisboa, Portugal"
          },
          services: {
            title: "Nossos Serviços",
            subtitle: "Soluções completas",
            services: []
          },
          decorative: {
            word1: "YOUR",
            word1_image: "https://images.unsplash.com/photo-1639515922883-9c419a10fd3a?w=800&q=80",
            word2: "DREAM",
            word2_image: "https://images.unsplash.com/photo-1526737511523-ba685f246e8c?w=800&q=80"
          }
        });
      } finally {
        setLoading(false);
      }
    };

    fetchHomeContent();
  }, []);

  // Handle hash navigation (e.g., /#contact, /#blog)
  useEffect(() => {
    if (location.hash && homeContent) {
      const hash = location.hash.substring(1); // Remove o #
      
      // Função auxiliar para obter posição absoluta do elemento na página
      const getAbsoluteTop = (element) => {
        let top = 0;
        let el = element;
        while (el) {
          top += el.offsetTop;
          el = el.offsetParent;
        }
        return top;
      };
      
      // ========== LÓGICA ESPECIAL PARA #contact (com estabilização do DOM) ==========
      if (hash === 'contact') {
        // Variáveis de controle
        let observer = null;
        let stabilityTimer = null;
        let fallbackTimer = null;
        let verificationTimer = null;
        let hasScrolledFinal = false;
        const MAX_WAIT_TIME = 800;  // Reduzido para scroll mais rápido
        const STABILITY_DELAY = 50;  // Reduzido para resposta mais ágil
        
        // Função para scrollar para o título da seção de contato
        const scrollToContact = () => {
          const section = document.getElementById('contact');
          const h2 = section?.querySelector('h2');
          const target = h2 || section;
          if (!target) return;
          
          const header = document.querySelector('header');
          const headerHeight = header ? header.offsetHeight : 80;
          
          // Calcular posição do target considerando header fixo (ajustado para título mais visível)
          const targetTop = target.getBoundingClientRect().top + window.pageYOffset - headerHeight - 60;
          
          window.scrollTo({
            top: Math.max(0, targetTop),
            behavior: 'smooth'
          });
        };
        
        // Função para limpar todos os recursos
        const cleanup = () => {
          if (observer) {
            observer.disconnect();
            observer = null;
          }
          if (stabilityTimer) {
            clearTimeout(stabilityTimer);
            stabilityTimer = null;
          }
          if (fallbackTimer) {
            clearTimeout(fallbackTimer);
            fallbackTimer = null;
          }
          if (verificationTimer) {
            clearTimeout(verificationTimer);
            verificationTimer = null;
          }
        };
        
        // Função para executar scroll final com verificação extra
        const executeScrollFinal = () => {
          if (hasScrolledFinal) return;
          hasScrolledFinal = true;
          
          // Limpar timers pendentes
          cleanup();
          
          // Executar scroll
          scrollToContact();
          
          // Verificação extra após 2 requestAnimationFrames + 100ms
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              verificationTimer = setTimeout(() => {
                const section = document.getElementById('contact');
                const h2 = section?.querySelector('h2');
                const target = h2 || section;
                if (!target) return;
                
                const header = document.querySelector('header');
                const headerHeight = header ? header.offsetHeight : 80;
                const currentTop = target.getBoundingClientRect().top;
                
                // Se o target não está bem posicionado (threshold tolerante de 80px)
                const expectedTop = headerHeight + 20;
                if (Math.abs(currentTop - expectedTop) > 80) {
                  scrollToContact(); // Reaplica apenas 1x
                }
              }, 100);
            });
          });
        };
        
        // MutationObserver para detectar quando o DOM estabiliza
        observer = new MutationObserver(() => {
          if (hasScrolledFinal) return;
          
          // Reiniciar timer a cada mutação
          if (stabilityTimer) clearTimeout(stabilityTimer);
          
          // Agendar scroll final após período de estabilidade
          stabilityTimer = setTimeout(() => {
            executeScrollFinal();
          }, STABILITY_DELAY);
        });
        
        // Iniciar observação do DOM (com attributes para maior robustez)
        observer.observe(document.body, { childList: true, subtree: true, attributes: true });
        
        // Timeout de segurança: se nenhuma mutação acontecer ou DOM não estabilizar
        fallbackTimer = setTimeout(() => {
          executeScrollFinal();
        }, MAX_WAIT_TIME);
        
        // Retornar cleanup function para o useEffect
        return cleanup;
      }
      
      // ========== COMPORTAMENTO PADRÃO PARA OUTRAS ÂNCORAS ==========
      const scrollToSection = (attempt = 0) => {
        const element = document.getElementById(hash);
        if (!element) {
          if (attempt < 10) {
            requestAnimationFrame(() => scrollToSection(attempt + 1));
          }
          return;
        }
        
        const header = document.querySelector('header');
        const headerHeight = header ? header.offsetHeight : 80;
        const h2 = element.querySelector('h2');
        
        if (h2) {
          const h2Top = getAbsoluteTop(h2);
          const offsetPosition = h2Top - headerHeight - 20;
          
          window.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: 'smooth'
          });
        } else {
          const elementTop = getAbsoluteTop(element);
          const offsetPosition = elementTop - headerHeight - 120;
          
          window.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: 'smooth'
          });
        }
      };
      
      setTimeout(() => scrollToSection(0), 150);
    }
  }, [homeContent, location.hash]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>{t.loading}</p>
      </div>
    );
  }

  // Prepare translated content
  const translatedContent = {
    hero: {
      title: getTranslated(homeContent.hero, 'title'),
      subtitle: getTranslated(homeContent.hero, 'subtitle'),
      image: homeContent.hero.image,
      ctaText: getTranslated(homeContent.hero, 'ctaText'),
      ctaLink: homeContent.hero.ctaLink
    },
    intro: {
      title: getTranslated(homeContent.intro, 'title'),
      description: getTranslated(homeContent.intro, 'description')
    },
    about: {
      title: getTranslated(homeContent.about, 'title'),
      description: getTranslated(homeContent.about, 'description'),
      highlight: getTranslated(homeContent.about, 'highlight'),
      image: homeContent.about.image
    },
    stats: {
      stat1_value: getTranslated(homeContent.stats, 'stat1_value'),
      stat1_label: getTranslated(homeContent.stats, 'stat1_label'),
      stat2_value: getTranslated(homeContent.stats, 'stat2_value'),
      stat2_label: getTranslated(homeContent.stats, 'stat2_label'),
      stat3_value: getTranslated(homeContent.stats, 'stat3_value'),
      stat3_label: getTranslated(homeContent.stats, 'stat3_label'),
      value_size: homeContent.stats.value_size || 'large',
      label_size: homeContent.stats.label_size || 'medium'
    },
    services: {
      title: getTranslated(homeContent.services, 'title'),
      subtitle: getTranslated(homeContent.services, 'subtitle'),
      services: homeContent.services.services.map(s => ({
        ...s,
        title: getTranslated(s, 'title'),
        description: getTranslated(s, 'description')
      }))
    },
    headerFooter: {
      ...homeContent.headerFooter,
      footerDescription: getTranslated(homeContent.headerFooter, 'footerDescription')
    },
    decorative: {
      word1: getTranslated(homeContent.decorative, 'word1'),
      word1_image: homeContent.decorative.word1_image,
      word2: getTranslated(homeContent.decorative, 'word2'),
      word2_image: homeContent.decorative.word2_image
    }
  };

  return (
    <>
      <SEO />
      <Header 
        companyName={homeContent.headerFooter.companyName}
        logo={homeContent.headerFooter.logo}
      />
      <HeroSection data={translatedContent.hero} />
      <CTABanner />
      <TrustStats />
      <ServicesSection data={translatedContent.services} />
      <TestimonialsSection />
      <RegionsSection />
      <AboutSection data={translatedContent.about} />
      <BlogSection />
      <ContactSection />
      <Footer data={translatedContent.headerFooter} />
    </>
  );
};

export default HomePage;
